import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../Redux/store";
import { getValue } from "../../../../utils/object.utils";

import { TableForm } from "../../../Table/TableForm/TableForm";
import { BtnSize, BtnType, Button } from "../../../Button";
import { StudentResultTitle } from "../../../../Constants/tableTitles/TableFormTitle/studentResult.tableForm.title";
import {
  setMetaData,
  setRemarks,
  updateGradeInResultEntry,
  updateMarksInResultEntry,
} from "../../../../Redux/Slices/studentResuilt.slice";
import {
  useDownloadStudentResult,
  useSaveStudentResults,
} from "../../../../hooks/resultsHooks/studentResult.hooks";
import { GridCellParams } from "@mui/x-data-grid";
import { SUBJECT_TYPE } from "../../../../Constants/Enums/Subject.enums";
import {
  setErrorMessage,
  setSuccessMessage,
} from "../../../../Redux/Slices/alert.slice";
import { parseApiError } from "../../../../Helper/apiError.helper";
import { SearchInput } from "../../../input/searchInput";

interface Props {
  resultId: number;
}
export const StudentResultform = ({ resultId }: Props) => {
  const dispatch = useDispatch();

  const { selectedStudent } = useSelector((state: RootState) => ({
    selectedStudent: state.studentResult.selectedStudentForResult,
  }));

  const metadata = getValue(selectedStudent, "metadata", {});

  const { isLoading: isSavingResults, mutate: saveStudentResult } =
    useSaveStudentResults({
      onSuccess: () => {
        dispatch(setSuccessMessage("Result saved successfully"));
      },
      onError: (error: Error) => {
        dispatch(setErrorMessage(parseApiError(error)));
      },
    });

  const {
    isLoading: isStudentResultDownloading,
    mutate: downloadStidemtResult,
  } = useDownloadStudentResult({
    onSuccess: (studentResult) => {
      if (studentResult) {
        window.open(studentResult, "_blank");
      }
    },
    onError: (error: Error) => {
      dispatch(setErrorMessage(parseApiError(error)));
    },
  });

  if (!selectedStudent) {
    return <></>;
  }

  const resultEntries = getValue(selectedStudent, "result_entries", []);

  const transformedResultEntry = resultEntries.map((entry: any) => ({
    ...entry,
    id: entry?.subject_id,
  }));

  const handleCellUpdate = (updateCell: any) => {
    const subjectId = getValue(updateCell, "subject_id");
    const marks = getValue(updateCell, "marks");
    const grade = getValue(updateCell, "grade");
    const changedField = getValue(updateCell, "changedField");

    if (changedField === "marks") {
      dispatch(updateMarksInResultEntry({ subjectId, value: marks }));
    }

    if (changedField === "grade") {
      dispatch(updateGradeInResultEntry({ subjectId, value: grade }));
    }
  };

  const handleSaveStudentResult = () => {
    const student_id = getValue(selectedStudent, "student_id");
    const result_entries = getValue(selectedStudent, "result_entries", []);

    const transformedResultEntries = result_entries.map((entry: any) => ({
      subject_id: entry?.subject_id,
      marks: entry?.marks,
      grade: entry?.grade,
    }));

    const metadata = getValue(selectedStudent, "metadata", {});
    saveStudentResult({
      student_id,
      result_id: resultId,
      result_entries: transformedResultEntries,
      remarks: selectedStudent?.remarks,
      height: metadata?.height,
      weight: metadata?.weight,
      attendance: metadata?.attendance,
    });
  };

  const handleStudentResult = () => {
    const studentId = getValue(selectedStudent, "student_id");

    downloadStidemtResult({
      studentId,
      resultId,
    });
  };

  const validations = [
    {
      marks: {
        validationFn: (validationFn: any) => {
          const fullMarks = getValue(validationFn, "full_marks", 0);
          const marks = getValue(validationFn, "marks", 0);

          return {
            isValid: !!marks && fullMarks >= marks && marks >= 0,
            errorMessage: "Marks can't be greater than full marks",
          };
        },
      },
    },
  ];

  const isCellEditable = (params: GridCellParams) => {
    const subjectType = getValue(params, "row.subject_type");
    const field = getValue(params, "field");

    if (field === "grade") {
      return subjectType === SUBJECT_TYPE.EXTRA_CURRICULAR;
    }
    if (field === "marks") {
      return subjectType !== SUBJECT_TYPE.EXTRA_CURRICULAR;
    }

    return true;
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "50px",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 50,
          alignItems: "end",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            gap: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 25,
              flex: 1,
            }}
          >
            <SearchInput
              label={"Attendance"}
              placeHolder="Enter attendance"
              value={metadata?.attendance}
              setValue={(value) => {
                dispatch(
                  setMetaData({
                    ...metadata,
                    attendance: +value,
                  })
                );
              }}
            />
            <SearchInput
              label={"Height"}
              placeHolder="Enter height"
              value={metadata?.height}
              setValue={(value) => {
                dispatch(
                  setMetaData({
                    ...metadata,
                    height: +value,
                  })
                );
              }}
              multiline
            />
            <SearchInput
              label={"Weight"}
              placeHolder="Enter weight"
              value={metadata?.weight}
              setValue={(value) => {
                dispatch(
                  setMetaData({
                    ...metadata,
                    weight: +value,
                  })
                );
              }}
              multiline
            />
          </div>
          <div style={{ flex: 1 }}>
            <SearchInput
              label={"Remarks"}
              placeHolder="Enter remarks"
              value={selectedStudent.remarks}
              setValue={(value) => {
                dispatch(setRemarks(value as string));
              }}
              multiline
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <Button
            text="Export result"
            btnType={BtnType.basic}
            btnSize={BtnSize.md}
            loading={isStudentResultDownloading}
            onClick={handleStudentResult}
          />

          <Button
            text="Save"
            btnType={BtnType.primary}
            btnSize={BtnSize.md}
            loading={isSavingResults}
            onClick={handleSaveStudentResult}
          />
        </div>
      </div>

      <TableForm
        columns={StudentResultTitle}
        initialRow={transformedResultEntry}
        onCellChange={handleCellUpdate}
        validations={validations}
        isCellEditable={isCellEditable}
      />
    </div>
  );
};
